"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Validator = void 0;
/* eslint-disable no-param-reassign */
const assert_never_1 = __importDefault(require("assert-never"));
const immer_1 = require("immer");
const common_1 = require("@tencent/tccp-frontend-contract/common");
const spu_1 = require("@tencent/tccp-frontend-contract/spu");
const trade_1 = require("@tencent/tccp-frontend-contract/trade");
const validator_1 = require("../types/validator");
const list_1 = require("../utils/list");
const strategy_1 = require("./strategy");
// 可以使用静态值渲染的出厂设置方式，允许回显
const ECHO_ALLOW_SUPPLIERS = new Set([
    trade_1.ValueSupplierEnum.Borrow,
    trade_1.ValueSupplierEnum.Multiplication,
    trade_1.ValueSupplierEnum.Sum,
    trade_1.ValueSupplierEnum.Specified,
    trade_1.ValueSupplierEnum.Max,
    trade_1.ValueSupplierEnum.Min,
    trade_1.ValueSupplierEnum.IntDivFloor,
    trade_1.ValueSupplierEnum.IntDivCeiling,
]);
// 后台设置的出厂方式，禁止回显
const ECHO_DISALLOW_SUPPLIERS = new Set([
    trade_1.ValueSupplierEnum.System,
    trade_1.ValueSupplierEnum.Empty,
]);
class Validator {
    /** 购买页基于参数清单和策略清单生成约束规格 */
    constructor(spuCode, scene, paramList, strategyList) {
        this.spuCode = spuCode;
        this.scene = scene;
        this.paramList = paramList;
        this.strategyList = strategyList;
        this.commonRules = {};
        this.echoParams = {};
        this.setupCommonRules();
    }
    setupCommonRules() {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const { paramList, strategyList } = this;
        const factoryAlgorithms = (_c = (_b = (_a = strategyList[trade_1.StrategyGroupEnum.Factory]) === null || _a === void 0 ? void 0 : _a.at(0)) === null || _b === void 0 ? void 0 : _b.Algorithms) !== null && _c !== void 0 ? _c : [];
        const factoryMap = new Map(factoryAlgorithms.map(x => [x.Key, x]));
        const echoList = (_g = (_f = (_e = (_d = this.strategyList[trade_1.StrategyGroupEnum.Buy]) === null || _d === void 0 ? void 0 : _d.at(0)) === null || _e === void 0 ? void 0 : _e.Description) === null || _f === void 0 ? void 0 : _f.EchoParamList) !== null && _g !== void 0 ? _g : [];
        this.echoParams = (0, list_1.listToRecord)(echoList, x => [x.UnitKey, x.Echo]);
        for (const param of paramList) {
            const { paramKey, spec } = param;
            const refUnit = spec.UnitReferencedSpec
                ? (_h = paramList.find(x => x.spu.SpuCode === param.spu.SpuCode && x.spec.SpecCode === spec.UnitReferencedSpec)) === null || _h === void 0 ? void 0 : _h.paramKey
                : undefined;
            // 初始化原始值域约束和值域收缩规格
            const rule = {
                restriction: spu_1.ClientRestriction.of(spec),
                constrictorRuleList: [],
                refUnit,
            };
            // 获取参数的出厂设置策略并设置到校验规则中
            const factory = factoryMap.get(paramKey) || trade_1.DefaultValueFactory.of(param);
            this.setupValueFactoryRule(paramKey, rule, factory);
            this.commonRules[paramKey] = rule;
        }
    }
    setupValueFactoryRule(paramKey, rule, algorithm) {
        const { Operator: operator, Value: value } = algorithm;
        const currentEchoParams = this.echoParams[paramKey];
        if (rule) {
            switch (operator) {
                // 指定值，默认用户无需输入
                case trade_1.ValueSupplierEnum.Specified: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.defaultValue = value;
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                // 借用值，默认无需输入
                case trade_1.ValueSupplierEnum.Borrow: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.ref = value;
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                // 规格乘积，默认无需输入
                case trade_1.ValueSupplierEnum.Multiplication: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.ref = value;
                    rule.refCalOp = '*';
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                // 规格求和，默认无需输入
                case trade_1.ValueSupplierEnum.Sum: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.ref = value;
                    rule.refCalOp = '+';
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                // 规格最大值，默认无需输入
                case trade_1.ValueSupplierEnum.Max: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.ref = value;
                    rule.refCalOp = 'max';
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                // 规格最小值，默认无需输入
                case trade_1.ValueSupplierEnum.Min: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.ref = value;
                    rule.refCalOp = 'min';
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                // 规格整数除法，默认无需输入
                case trade_1.ValueSupplierEnum.IntDivFloor: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.ref = value;
                    rule.refCalOp = 'int-div-floor';
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                case trade_1.ValueSupplierEnum.IntDivCeiling: {
                    rule.isStatic = true;
                    rule.isRequired = false;
                    rule.ref = value;
                    rule.refCalOp = 'int-div-ceiling';
                    rule.isHidden = true;
                    rule.isCalculable = true;
                    break;
                }
                // 系统生成和发货回填，无需输入，默认隐藏
                case trade_1.ValueSupplierEnum.Shipping: {
                    rule.isStatic = true;
                    rule.isHidden = true;
                    rule.isRequired = false;
                    rule.isCalculable = false;
                    break;
                }
                case trade_1.ValueSupplierEnum.System: {
                    rule.isStatic = true;
                    rule.isHidden = true;
                    rule.isRequired = false;
                    rule.isCalculable = false;
                    break;
                }
                case trade_1.ValueSupplierEnum.Input: {
                    delete rule.isStatic;
                    rule.isRequired = true;
                    if (value !== undefined && value !== null) {
                        rule.defaultValue = value;
                    }
                    rule.isCalculable = true;
                    break;
                }
                case trade_1.ValueSupplierEnum.PickUp: {
                    delete rule.isStatic;
                    rule.isRequired = false;
                    if (value !== undefined && value !== null) {
                        rule.defaultValue = value;
                    }
                    rule.isCalculable = true;
                    break;
                }
                case trade_1.ValueSupplierEnum.Empty: {
                    rule.isHidden = true;
                    rule.isRequired = false;
                    rule.isStatic = true;
                    rule.isCalculable = false;
                    break;
                }
                default: {
                    (0, assert_never_1.default)(operator);
                }
            }
            // echoParams 也会影响默认展示的效果
            if (ECHO_ALLOW_SUPPLIERS.has(operator) && currentEchoParams) {
                rule.isHidden = false;
            }
            else if (ECHO_DISALLOW_SUPPLIERS.has(operator)) {
                rule.isHidden = true;
            }
            // 限制一下，如果是要求用户输入的，禁止隐藏
            // 放在最后兜底
            if (operator === trade_1.ValueSupplierEnum.Input) {
                delete rule.isHidden;
            }
        }
    }
    validate(prevValues, key, nextValue) {
        let values;
        if (typeof key === 'undefined') {
            values = prevValues;
        }
        else {
            values = Object.assign(Object.assign({}, prevValues), { [key]: nextValue });
        }
        const result = {};
        const effectiveRules = this.getEffectiveRules(values);
        Object.entries(values).forEach(([k, v]) => {
            const rule = effectiveRules[k];
            if (rule) {
                const { isHidden, isRequired, isStatic, restriction, constrictorRuleList } = rule;
                // 隐藏的和静态的规格不参加校验
                if (isHidden || isStatic) {
                    return;
                }
                // 如果不是必填的且没有填(v 是 falsy的情况下都认为没填)，则不校验
                if (!isRequired && !v) {
                    return;
                }
                if (isRequired && (v === undefined || v === null)) {
                    const entity = {
                        paramKey: k,
                        actual: v,
                        operator: validator_1.ErrorMessageOperator.REQUIRED,
                        expected: true,
                    };
                    result[k] = {
                        entity,
                        rule,
                    };
                    return;
                }
                // 先判断基本的值域
                switch (restriction.type) {
                    case spu_1.RestrictionEnum.Choice: {
                        const values = restriction.choices.map(x => x.value);
                        if (!values.includes(v)) {
                            const entity = {
                                paramKey: k,
                                actual: v,
                                operator: validator_1.ErrorMessageOperator.IN,
                                expected: values,
                            };
                            result[k] = {
                                entity,
                                rule,
                            };
                            return;
                        }
                        break;
                    }
                    case spu_1.RestrictionEnum.Range: {
                        const { min, max } = restriction.range;
                        if (!(v >= min && v <= max)) {
                            const entity = {
                                paramKey: k,
                                actual: v,
                                operator: validator_1.ErrorMessageOperator.BETWEEN,
                                expected: [min, max],
                            };
                            result[k] = {
                                entity,
                                rule,
                            };
                            return;
                        }
                        break;
                    }
                    case spu_1.RestrictionEnum.Regex: {
                        const reg = new RegExp(restriction.regexp);
                        if (!reg.test(v)) {
                            const entity = {
                                paramKey: k,
                                actual: v,
                                operator: validator_1.ErrorMessageOperator.MATCH,
                                expected: restriction.regexp,
                            };
                            result[k] = {
                                entity,
                                rule,
                            };
                            return;
                        }
                        break;
                    }
                    case spu_1.RestrictionEnum.Ref: {
                        break;
                    }
                }
                // 前面合格了，判断是否满足值域收缩的约束
                for (const constrictorRule of constrictorRuleList) {
                    const { operator, value } = constrictorRule;
                    switch (operator) {
                        case trade_1.ValueConstrictorEnum.Subset: {
                            if (!value.includes(v)) {
                                const entity = {
                                    paramKey: k,
                                    actual: v,
                                    operator: validator_1.ErrorMessageOperator.IN,
                                    expected: value,
                                };
                                result[k] = {
                                    entity,
                                    rule,
                                };
                                return;
                            }
                            break;
                        }
                        case trade_1.ValueConstrictorEnum.Exclude: {
                            if (value.includes(v)) {
                                const entity = {
                                    paramKey: k,
                                    actual: v,
                                    operator: validator_1.ErrorMessageOperator.NOT_IN,
                                    expected: value,
                                };
                                result[k] = {
                                    entity,
                                    rule,
                                };
                                return;
                            }
                            break;
                        }
                        case trade_1.ValueConstrictorEnum.Range: {
                            const [min, max] = value;
                            if (!(v >= min && v <= max)) {
                                const entity = {
                                    paramKey: k,
                                    actual: v,
                                    operator: validator_1.ErrorMessageOperator.BETWEEN,
                                    expected: [min, max],
                                };
                                result[k] = {
                                    entity,
                                    rule,
                                };
                                return;
                            }
                            break;
                        }
                    }
                }
            }
        });
        if (typeof key !== 'undefined') {
            return result[key];
        }
        return result;
    }
    /**
     * 根据当前的选项获取实际生效的规则
     */
    getEffectiveRules(values) {
        // 筛选规格约束
        const globalConstraintStrategyList = this.strategyList[trade_1.StrategyGroupEnum.GlobalConstraint];
        const constraintStrategyList = this.strategyList[trade_1.StrategyGroupEnum.Constraint];
        // 补充spu信息
        const valuesWithSpu = Object.assign(Object.assign({}, values), { spu: this.spuCode, scene: this.scene });
        const valueAlgorithms = [...globalConstraintStrategyList, ...constraintStrategyList]
            .filter(({ Filters: filters }) => filters.every(filter => {
            var _a;
            return (0, strategy_1.compileFilterToBoolean)(valuesWithSpu[filter.Key], filter.Operator, (_a = filter.Value) !== null && _a !== void 0 ? _a : (0, common_1.getFilterValues)(filter));
        }))
            .map(({ Algorithms }) => Algorithms)
            .flat();
        return this.effectiveRulesProducer(valueAlgorithms)(this.commonRules);
    }
    effectiveRulesProducer(valueAlgorithms) {
        return (0, immer_1.produce)(draft => {
            // 遍历valueAlgorithm，修改现有的规则
            for (const algorithm of valueAlgorithms) {
                const { Key: key, Operator: operator, Value: value, Name: name } = algorithm;
                if (name === trade_1.AlgorithmEnum.ValueFactory) {
                    this.setupValueFactoryRule(key, draft[key], algorithm);
                }
                else if (name === trade_1.AlgorithmEnum.ValueConstrictor) {
                    // 修改值域
                    draft[key].constrictorRuleList.push({
                        operator,
                        value,
                    });
                }
            }
        });
    }
}
exports.Validator = Validator;
